import type { Except } from "type-fest";
import { z } from "zod";
import { contentBlockZodSchema } from "../acquire/content-block";
import { isoAndUnixZodSchema, nowIsoAndUnix } from "../date-time/iso-and-unix";
import { timeRangeZodSchema } from "../date-time/time-range";
import { entityStateZodSchema } from "../entity-status";
import { metaContentZodSchema } from "../meta-content/meta-content";
import { pickedOptionalZodSchema } from "../optionals/picked-optional";
import { orderGroupIdZodSchema } from "../order-groups/order-group-id";
import { orderIdZodSchema } from "../orders/order-id";
import { priceZodSchema } from "../prices/price";
import { productIdZodSchema } from "../products/product-id";
import { providerIdZodSchema } from "../providers/provider-id";
import type { Translatable} from "../translations/translation";
import { translation } from "../translations/translation";
import { systemUserZodSchema } from "../users/system-user";
import { userIdZodSchema } from "../users/user-id";
import { bulkPurchaseGroupIdZodSchema } from "./bulk-purchase-group-id";
import type { TicketId} from "./ticket-id";
import { ticketIdFactory, ticketIdZodSchema } from "./ticket-id";

type TicketStatus = "ACTIVE" | "USED" | "EXPIRED" | "CANCELLED";
const ticketStatusZodSchema = z.enum([
    "ACTIVE",
    "USED",
    "EXPIRED",
    "CANCELLED",
]);

const ticketEventZodSchema = z.object({
    type: z.enum([
        "REDEEMED",
        "CREATED",
        "REVERTED",
        "PASSED",
        "SCANNED_USED",
        "SCANNED_UNUSED",
    ]),
    _createdAt: isoAndUnixZodSchema,
    userId: z.union([userIdZodSchema, systemUserZodSchema]),
    scanner: z.string().optional(),
});
type TicketEvent = z.infer<typeof ticketEventZodSchema>;

const _ticketBaseZodSchema = z.object({
    _id: ticketIdZodSchema,
    _type: z.literal("TICKET"),
    _createdAt: isoAndUnixZodSchema,
    _status: entityStateZodSchema,
    meta: metaContentZodSchema,
});
const ticketVoucherZodSchema = _ticketBaseZodSchema.merge(
    z.object({
        type: z.literal("VOUCHER").optional(),
        productId: productIdZodSchema,
        providerId: providerIdZodSchema,
        orderId: orderIdZodSchema.optional(),
        orderGroupId: orderGroupIdZodSchema.optional(),
        bulkId: bulkPurchaseGroupIdZodSchema.optional(),
        code: z.string().min(1),
        valid: timeRangeZodSchema,
        status: ticketStatusZodSchema,
        price: priceZodSchema,
        shortLink: z.string().min(1),
        contentBlock: z.array(contentBlockZodSchema),
        history: z.array(ticketEventZodSchema),
        optionals: z.array(pickedOptionalZodSchema),
    }),
);
type TicketVoucher = z.infer<typeof ticketVoucherZodSchema>;
const isVoucherTicket = (ticket: Ticket): ticket is TicketVoucher => {
    return ticket.type === "VOUCHER" || typeof ticket.type === "undefined";
};

const ticketGiftCardZodSchema = _ticketBaseZodSchema.merge(
    z.object({
        type: z.literal("GIFTCARD"),
        version: z.number().min(0),
        productId: productIdZodSchema,
        providerId: providerIdZodSchema,
        orderId: orderIdZodSchema.optional(),
        orderGroupId: orderGroupIdZodSchema.optional(),
        bulkId: bulkPurchaseGroupIdZodSchema.optional(),
        code: z.string().min(1),
        valid: timeRangeZodSchema,
        status: ticketStatusZodSchema,
        amount: priceZodSchema,
        amountUsed: priceZodSchema,
        shortLink: z.string().min(1),
        contentBlock: z.array(contentBlockZodSchema),
        optionals: z.array(pickedOptionalZodSchema),
    }),
);
type TicketGiftCard = z.infer<typeof ticketGiftCardZodSchema>;
const isGiftCardTicket = (ticket: Ticket): ticket is TicketGiftCard => {
    return ticket.type === "GIFTCARD";
};

//DEMO QUICK QUICK
const _ticketEventWithExtrasZodSchema = ticketEventZodSchema.merge(
    z.object({
        currentTicket: ticketVoucherZodSchema,
    }),
);
type TicketEventWithExtras = z.infer<typeof _ticketEventWithExtrasZodSchema>;

type ticketVoucherFactoryParams = Except<
    TicketVoucher,
    "_id" | "_createdAt" | "_type" | "_status" | "type"
> & {
    _id?: TicketId;
};

const ticketVoucherFactory = (
    params: ticketVoucherFactoryParams,
): TicketVoucher => {
    const now = nowIsoAndUnix();
    return {
        ...params,
        _id: params._id ?? ticketIdFactory(),
        _createdAt: now,
        _status: "ACTIVE",
        _type: "TICKET",
        type: "VOUCHER",
    };
};

type TicketGiftCardFactoryParams = Except<
    TicketGiftCard,
    "_id" | "_createdAt" | "_type" | "_status" | "type"
> & {
    _id?: TicketId;
};
const ticketGiftCardFactory = (
    params: TicketGiftCardFactoryParams,
): TicketGiftCard => {
    const now = nowIsoAndUnix();
    return {
        ...params,
        _id: params._id ?? ticketIdFactory(),
        _createdAt: now,
        _status: "ACTIVE",
        _type: "TICKET",
        type: "GIFTCARD",
    };
};

// type Ticket = TicketVoucher;
// type TicketModel = Ticket | TicketGiftCard;
const _ticketZodSchema = z.union([
    ticketVoucherZodSchema,
    ticketGiftCardZodSchema,
]);
type Ticket = z.infer<typeof _ticketZodSchema>;

const ticketTypeTranslations: Record<
    NonNullable<Ticket["type"]>,
    { singular: Translatable; plural: Translatable }
> = {
    VOUCHER: {
        singular: translation({
            sv: "värdecheck",
            en: "voucher",
        }),
        plural: translation({
            sv: "värdecheckar",
            en: "vouchers",
        }),
    },
    GIFTCARD: {
        singular: translation({
            sv: "presentkort",
            en: "giftcard",
        }),
        plural: translation({
            sv: "presentkort",
            en: "giftcards",
        }),
    },
};

export {
    isGiftCardTicket,
    isVoucherTicket,
    ticketEventZodSchema,
    ticketGiftCardFactory,
    ticketTypeTranslations,
    ticketVoucherFactory,
};
export type {
    Ticket,
    TicketEvent,
    TicketEventWithExtras,
    TicketGiftCard,
    TicketStatus,
    TicketVoucher,
};
